import { DataMode } from '../types/census';

export interface NewsItem {
  id: string;
  title: string;
  url: string;
  domain: string;
  points: number;
  comments: number;
  created: string;
  discussUrl: string;
}

export interface NewsResult {
  mode: DataMode | 'unavailable';
  asOf: string;
  items: NewsItem[];
}

export interface TrendingModel {
  id: string;
  likes: number;
  downloads: number;
  task: string | null;
}

export interface TrendingResult {
  mode: DataMode | 'unavailable';
  asOf: string;
  models: TrendingModel[];
}

interface HNHit {
  objectID: string;
  title?: string;
  url?: string;
  points?: number;
  num_comments?: number;
  created_at: string;
}

const QUERIES = ['LLM', 'AI agent', 'AI model', 'open weights', 'GPU'];
export const HN_SEARCH = 'https://hn.algolia.com/api/v1/search_by_date';
export const HF_TRENDING = 'https://huggingface.co/api/models?sort=trendingScore&limit=30';

function stamp(): string {
  return `fetched ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'news.ycombinator.com';
  }
}

let newsCache: Promise<NewsResult> | null = null;

export function getAiNews(): Promise<NewsResult> {
  if (newsCache) return newsCache;
  newsCache = (async () => {
    try {
      const results = await Promise.all(
        QUERIES.map((q) =>
          fetch(`${HN_SEARCH}?tags=story&query=${encodeURIComponent(q)}&numericFilters=points>25&hitsPerPage=25`)
            .then((r) => (r.ok ? r.json() : { hits: [] }))
            .catch(() => ({ hits: [] })),
        ),
      );
      const seen = new Set<string>();
      const items: NewsItem[] = [];
      results.forEach((res: { hits?: HNHit[] }) =>
        (res.hits ?? []).forEach((h) => {
          if (!h.title || seen.has(h.objectID)) return;
          seen.add(h.objectID);
          const discussUrl = `https://news.ycombinator.com/item?id=${h.objectID}`;
          const url = h.url ?? discussUrl;
          items.push({ id: h.objectID, title: h.title, url, domain: domainOf(url), points: h.points ?? 0, comments: h.num_comments ?? 0, created: h.created_at, discussUrl });
        }),
      );
      if (items.length === 0) throw new Error('Empty');
      items.sort((a, b) => b.created.localeCompare(a.created));
      return { mode: 'live' as const, asOf: stamp(), items: items.slice(0, 40) };
    } catch {
      newsCache = null;
      return { mode: 'unavailable' as const, asOf: '', items: [] };
    }
  })();
  return newsCache;
}

let trendCache: Promise<TrendingResult> | null = null;

export function getTrendingModels(): Promise<TrendingResult> {
  if (trendCache) return trendCache;
  trendCache = (async () => {
    try {
      const res = await fetch(HF_TRENDING);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = (await res.json()) as Array<{ id: string; likes?: number; downloads?: number; pipeline_tag?: string }>;
      if (!Array.isArray(raw) || raw.length === 0) throw new Error('Empty');
      return { mode: 'live' as const, asOf: stamp(), models: raw.map((m) => ({ id: m.id, likes: m.likes ?? 0, downloads: m.downloads ?? 0, task: m.pipeline_tag ?? null })) };
    } catch {
      trendCache = null;
      return { mode: 'unavailable' as const, asOf: '', models: [] };
    }
  })();
  return trendCache;
}

export function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
